import {useCallback, useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useFilterStore} from "../contexts/FilterStore.tsx";
import {readRecentClientIds, withRecentClientId, writeRecentClientIds} from "../lib/recentClients.ts";

export const CLIENT_ID_PARAM = 'clientId';

interface SelectClientOptions {
    resetParams?: string[];
}

export interface PartnerClientSelection {
    selectedClientId: string | null;
    recentClientIds: string[];
    selectClient: (clientId: string | null, options?: SelectClientOptions) => void;
}

export function usePartnerClientSelection(userId: string): PartnerClientSelection {
    const {state, dispatch} = useFilterStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const [recentClientIds, setRecentClientIds] = useState<string[]>(() => readRecentClientIds(userId));

    const urlClientId = searchParams.get(CLIENT_ID_PARAM);
    const storeClientId = state.common.customerId;
    const pendingSelectionRef = useRef<{ clientId: string | null } | null>(null);
    const pendingSelection = pendingSelectionRef.current;
    const selectedClientId = pendingSelection ? pendingSelection.clientId : (urlClientId || storeClientId || null);

    useEffect(() => {
        if (pendingSelectionRef.current) {
            if ((urlClientId || null) !== pendingSelectionRef.current.clientId) return;
            pendingSelectionRef.current = null;
        }
        if (urlClientId && urlClientId !== storeClientId) {
            dispatch({type: 'SET_FILTERS', payload: {common: {customerId: urlClientId}}});
        } else if (!urlClientId && storeClientId) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set(CLIENT_ID_PARAM, storeClientId);
                return next;
            }, {replace: true});
        }
    }, [urlClientId, storeClientId, dispatch, setSearchParams]);

    const selectClient = useCallback((clientId: string | null, options?: SelectClientOptions) => {
        pendingSelectionRef.current = {clientId};
        dispatch({type: 'SET_FILTERS', payload: {common: {customerId: clientId}}});
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (clientId) {
                next.set(CLIENT_ID_PARAM, clientId);
            } else {
                next.delete(CLIENT_ID_PARAM);
            }
            options?.resetParams?.forEach((param) => next.delete(param));
            return next;
        }, {replace: true});
        if (clientId) {
            const nextRecentClientIds = withRecentClientId(recentClientIds, clientId);
            writeRecentClientIds(userId, nextRecentClientIds);
            setRecentClientIds(nextRecentClientIds);
        }
    }, [dispatch, setSearchParams, recentClientIds, userId]);

    return {selectedClientId, recentClientIds, selectClient};
}
